import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Star, Plus, Save, Trash2, Eye, EyeOff, Loader2, MessageSquareQuote } from "lucide-react";
import { api, authHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import { Panel, inputCls } from "@/components/admin/cc";
import { ReviewLinkSetting } from "@/components/admin/ReviewRequest";

const RatingPicker = ({ value, onChange, testid }) => (
  <div className="inline-flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => onChange(i)}
        data-testid={`${testid}-${i}`}
        className="p-0.5"
        aria-label={`${i} sterren`}
      >
        <Star className={`w-5 h-5 ${i <= value ? "fill-amber-400 text-amber-400" : "text-cc-faint"}`} />
      </button>
    ))}
  </div>
);

const ReviewRow = ({ r, onSaved, onDeleted }) => {
  const [draft, setDraft] = useState(r);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async (patch) => {
    setBusy(true);
    try {
      const body = patch || { author: draft.author, location: draft.location, rating: draft.rating, text: draft.text, order: Number(draft.order) || 0 };
      const { data } = await api.patch(`/admin/reviews/${r.id}`, body, { headers: authHeaders() });
      onSaved(data);
      toast.success("Beoordeling opgeslagen.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = () => save({ published: !draft.published });

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/reviews/${r.id}`, { headers: authHeaders() });
      onDeleted(r.id);
      toast.success("Beoordeling verwijderd.");
    } catch {
      toast.error("Verwijderen mislukt.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel className="p-5" data-testid={`admin-review-${r.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <RatingPicker value={draft.rating} onChange={(v) => set("rating", v)} testid={`review-rating-${r.id}`} />
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${draft.published ? "bg-cc-pos/15 text-cc-pos" : "bg-cc-faint/15 text-cc-faint"}`}>
          {draft.published ? "Gepubliceerd" : "Verborgen"}
        </span>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <input value={draft.author} onChange={(e) => set("author", e.target.value)} placeholder="Naam" data-testid={`review-author-${r.id}`} className={inputCls} />
        <input value={draft.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="Gemeente" data-testid={`review-location-${r.id}`} className={inputCls} />
        <input type="number" value={draft.order ?? 0} onChange={(e) => set("order", e.target.value)} placeholder="Volgorde" data-testid={`review-order-${r.id}`} className={inputCls} />
      </div>
      <textarea value={draft.text} onChange={(e) => set("text", e.target.value)} rows={3} placeholder="Beoordelingstekst" data-testid={`review-text-${r.id}`} className={`${inputCls} resize-none mb-4`} />
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => save()} disabled={busy} data-testid={`review-save-${r.id}`} className="inline-flex items-center gap-2 rounded-xl bg-cc-accent text-white px-4 py-2 font-body text-sm font-semibold hover:bg-cc-accenth transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Opslaan
        </button>
        <button onClick={togglePublish} disabled={busy} data-testid={`review-toggle-${r.id}`} className="inline-flex items-center gap-2 rounded-xl border border-cc-border px-4 py-2 font-body text-sm font-medium text-cc-sub hover:border-cc-accent hover:text-cc-accent transition-colors disabled:opacity-60">
          {draft.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} {draft.published ? "Verbergen" : "Publiceren"}
        </button>
        <button onClick={remove} disabled={busy} data-testid={`review-delete-${r.id}`} className="ml-auto inline-flex items-center gap-2 rounded-xl border border-cc-border px-4 py-2 font-body text-sm font-medium text-cc-hot hover:bg-cc-hot/10 transition-colors disabled:opacity-60">
          <Trash2 className="w-4 h-4" /> Verwijderen
        </button>
      </div>
    </Panel>
  );
};

export default function AdminReviews() {
  const { logout } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ author: "", location: "", rating: 5, text: "", published: true, order: 0 });
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/reviews", { headers: authHeaders() });
      setReviews(data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.author.trim() || !form.text.trim()) {
      toast.error("Vul minstens een naam en tekst in.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/reviews", { ...form, order: Number(form.order) || 0 }, { headers: authHeaders() });
      setReviews((rs) => [...rs, data].sort((a, b) => (a.order || 0) - (b.order || 0)));
      setForm({ author: "", location: "", rating: 5, text: "", published: true, order: 0 });
      toast.success("Beoordeling toegevoegd.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toevoegen mislukt.");
    } finally {
      setCreating(false);
    }
  };

  const onSaved = (updated) => setReviews((rs) => rs.map((r) => (r.id === updated.id ? updated : r)).sort((a, b) => (a.order || 0) - (b.order || 0)));
  const onDeleted = (id) => setReviews((rs) => rs.filter((r) => r.id !== id));

  const published = reviews.filter((r) => r.published).length;

  return (
    <AdminLayout title="Beoordelingen" subtitle="Beheer de klantbeoordelingen die op de website verschijnen">
      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <ReviewLinkSetting />
          <Panel className="p-6">
            <div className="flex items-center gap-2 mb-5">
              <Plus className="w-5 h-5 text-cc-accent" />
              <h2 className="font-heading font-bold text-lg text-cc-ink">Nieuwe beoordeling</h2>
            </div>
            <label className="block font-body text-sm font-medium text-cc-sub mb-2">Naam</label>
            <input value={form.author} onChange={(e) => setF("author", e.target.value)} placeholder="Bv. Marie Dubois" data-testid="new-review-author" className={`${inputCls} mb-4`} />
            <label className="block font-body text-sm font-medium text-cc-sub mb-2">Gemeente (optioneel)</label>
            <input value={form.location} onChange={(e) => setF("location", e.target.value)} placeholder="Bv. Kraainem" data-testid="new-review-location" className={`${inputCls} mb-4`} />
            <label className="block font-body text-sm font-medium text-cc-sub mb-2">Sterren</label>
            <div className="mb-4"><RatingPicker value={form.rating} onChange={(v) => setF("rating", v)} testid="new-review-rating" /></div>
            <label className="block font-body text-sm font-medium text-cc-sub mb-2">Beoordelingstekst</label>
            <textarea value={form.text} onChange={(e) => setF("text", e.target.value)} rows={4} placeholder="Wat zei de klant over VHM Renovation?" data-testid="new-review-text" className={`${inputCls} resize-none mb-4`} />
            <label className="flex items-center gap-2 font-body text-sm text-cc-sub mb-5">
              <input type="checkbox" checked={form.published} onChange={(e) => setF("published", e.target.checked)} data-testid="new-review-published" className="w-4 h-4 accent-cc-accent" />
              Meteen publiceren op de website
            </label>
            <button onClick={create} disabled={creating} data-testid="new-review-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-cc-accent text-white px-6 py-3 font-body font-semibold hover:bg-cc-accenth transition-colors disabled:opacity-60">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Beoordeling toevoegen
            </button>
            <p className="mt-4 font-body text-xs text-cc-faint">
              {published} van {reviews.length} beoordeling(en) zichtbaar op de website. De sterren en het gemiddelde verschijnen op de homepage en de lokale schilderpagina's, en worden als Review-schema meegegeven aan Google.
            </p>
          </Panel>
        </div>

        <div className="lg:col-span-3 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-cc-faint py-10"><Loader2 className="w-5 h-5 animate-spin" /> Laden…</div>
          ) : reviews.length === 0 ? (
            <Panel className="p-10 text-center">
              <MessageSquareQuote className="w-10 h-10 text-cc-faint mx-auto mb-3" />
              <p className="font-body text-sm text-cc-faint">Nog geen beoordelingen. Voeg er links een toe.</p>
            </Panel>
          ) : (
            reviews.map((r) => <ReviewRow key={r.id} r={r} onSaved={onSaved} onDeleted={onDeleted} />)
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
